import React from 'react'
import { useEinsatz } from './EinsatzContext'
import { oeffneEinsatzWahl } from './EinsatzWahl'
import { EinsatzOption, einsatzStatusInfo } from './helpers'

export interface RequireEinsatzProps {
  /** Seite funktioniert auch ohne Einsatz (z. B. Risikobewertung): Kinder werden immer gerendert. */
  optional?: boolean
  /** Eigene Überschrift/Erklärung des Platzhalters. */
  hinweis?: React.ReactNode
  /** Nur diese Kandidaten im Platzhalter anbieten (z. B. e => e.laufend). */
  filter?: (e: EinsatzOption) => boolean
  /** Kinder als Funktion: bekommen den gewählten Einsatz (nie null). */
  children: React.ReactNode | ((einsatz: EinsatzOption) => React.ReactNode)
}

/**
 * Wrapper für Seiten, die an einen Einsatz gebunden sind. Ohne Wahl: einheitlicher Platzhalter mit Auswahl
 * (ersetzt die seiteneigenen "Bitte oben einen Einsatz auswählen"-Texte); sonst die Seite.
 *   <RequireEinsatz>{e => <Dashboard einsatz={e} />}</RequireEinsatz>
 */
export function RequireEinsatz({ optional, hinweis, filter, children }: RequireEinsatzProps) {
  const ew = useEinsatz()
  if (optional) return <>{typeof children === 'function' ? (ew.einsatz ? children(ew.einsatz) : null) : children}</>
  if (ew.einsatz) return <>{typeof children === 'function' ? children(ew.einsatz) : children}</>

  const kandidaten = filter ? ew.einsaetze.filter(filter) : ew.einsaetze
  return (
    <div className="ui-req" role="status">
      <div className="ui-req-box">
        <h2 className="ui-req-h">{ew.loading ? 'Einsätze werden geladen …' : 'Kein Einsatz gewählt'}</h2>
        {!ew.loading && <p className="ui-req-p">{hinweis ?? 'Bitte einen Einsatz wählen, um diese Seite zu nutzen.'}</p>}
        {ew.fehler && (
          <p className="ui-req-err" role="alert">
            {ew.fehler} <button type="button" className="ui-ew-link" onClick={() => void ew.reload()}>Erneut versuchen</button>
          </p>
        )}
        {!ew.loading && !ew.fehler && !kandidaten.length && <p className="ui-req-p">Derzeit ist kein Einsatz verfügbar.</p>}
        {!ew.locked && kandidaten.length > 0 && (
          <div className="ui-req-list">
            {kandidaten.slice(0, 8).map(e => (
              <button key={e.id} type="button" className="ui-req-it" onClick={() => void ew.setEinsatz(e.id)}>
                <span className="ui-ew-it-t">{e.titel}</span>
                <span className="ui-ew-it-s">{einsatzStatusInfo(e.status).label}</span>
              </button>
            ))}
            {kandidaten.length > 8 && <button type="button" className="ui-ew-link" onClick={oeffneEinsatzWahl}>Alle {kandidaten.length} Einsätze anzeigen</button>}
          </div>
        )}
        {ew.locked && ew.lockedHint && <p className="ui-req-p">{ew.lockedHint}</p>}
      </div>
    </div>
  )
}
